<template>
  <b-modal
    :visible="visible"
    title="Finalizar Ocorrência"
    size="md"
    centered
    lazy
    hide-footer
    @hide="$emit('update:visible', false)"
    @show="onShow"
  >
    <div class="finalize-modal-body">
      <div class="finalize-modal-warning mb-3">
        <feather-icon
          icon="AlertTriangleIcon"
          size="18"
          class="mr-50"
        />
        Esta ação irá finalizar a ocorrência aberta. Informe o desfecho antes de confirmar.
      </div>

      <div class="form-group">
        <label><span class="required">*</span> Desfecho</label>
        <v-select
          v-model="form.outcome_id"
          :options="outcomes"
          :reduce="(o) => o.id"
          label="name"
          placeholder="Selecione o desfecho..."
          class="vselect"
          :loading="loadingOutcomes"
          @input="clearPendencies"
        />
        <small
          v-if="showErrors && !form.outcome_id"
          class="text-danger"
        >
          Desfecho é obrigatório.
        </small>
      </div>

      <div class="form-group">
        <label>Observação</label>
        <textarea
          v-model="form.outcome_observation"
          class="form-control"
          rows="3"
          placeholder="Observação sobre a finalização (opcional)..."
          maxlength="2000"
        />
      </div>

      <div
        v-if="violations.length"
        class="finalize-pendencies"
      >
        <div class="finalize-pendencies-title">
          <feather-icon
            icon="XCircleIcon"
            size="15"
            class="mr-50"
          />
          Não é possível finalizar{{ outcomeName ? ` como ${outcomeName}` : '' }}
        </div>
        <ul class="finalize-pendencies-list">
          <li
            v-for="(v, i) in violations"
            :key="`e${i}`"
          >
            <b-form-checkbox
              v-if="canOverride && v.allows_override"
              :checked="overrideIds.includes(v.rule_id)"
              class="d-inline-block mr-50"
              @change="toggleOverride(v.rule_id)"
            />
            {{ v.message }}
            <button
              v-if="canOpen(v)"
              type="button"
              class="btn btn-link btn-sm p-0 ml-50 align-baseline"
              @click="$emit('open-pendency', v)"
            >
              Resolver
            </button>
          </li>
        </ul>

        <div
          v-if="canOverride && hasOverridable"
          class="mt-1"
        >
          <small class="text-muted d-block mb-25">
            Marque a pendência que deseja dispensar e justifique — a dispensa fica registrada.
          </small>
          <textarea
            v-model="overrideReason"
            class="form-control"
            rows="2"
            placeholder="Justificativa da dispensa..."
            maxlength="1000"
          />
        </div>
      </div>

      <div
        v-if="warnings.length"
        class="finalize-warn-block"
      >
        <div class="finalize-pendencies-title">
          <feather-icon
            icon="AlertCircleIcon"
            size="15"
            class="mr-50"
          />
          Avisos
        </div>
        <ul class="finalize-pendencies-list">
          <li
            v-for="(w, i) in warnings"
            :key="`w${i}`"
          >
            {{ w.message }}
          </li>
        </ul>
      </div>

      <OccurrenceEquipmentPanel
        :occurrence-id="occurrenceId"
        readonly
      />

      <div
        v-if="activeDispatches > 0"
        class="finalize-warn"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="14"
          class="mr-50"
        />
        {{ activeDispatches }}
        {{ activeDispatches > 1 ? "equipes serão liberadas" : "equipe será liberada" }}
        ao finalizar.
      </div>

      <div
        class="d-flex justify-content-end mt-1"
        style="gap: 8px"
      >
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="$emit('update:visible', false)"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="14"
            class="mr-50"
          />
          Voltar
        </button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="saving"
          @click="confirm"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="CheckCircleIcon"
            size="14"
            class="mr-50"
          />
          {{ confirmLabel }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import vSelect from 'vue-select'
import { BFormCheckbox, BSpinner } from 'bootstrap-vue'
import OccurrenceEquipmentPanel from '@/components/OccurrenceEquipmentPanel.vue'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

export default {
  name: 'FinalizeOccurrenceModal',
  components: {
    vSelect, OccurrenceEquipmentPanel, BFormCheckbox, BSpinner,
  },
  mixins: [ApiErrorMixin],

  props: {
    visible: { type: Boolean, default: false },
    disciplineId: { type: Number, default: null },
    activeDispatches: { type: Number, default: 0 },
    occurrenceId: { type: [Number, String], default: null },
  },

  data() {
    return {
      outcomes: [],
      loadingOutcomes: false,
      showErrors: false,
      saving: false,
      violations: [],
      warnings: [],
      requiresConfirmation: false,
      overrideIds: [],
      overrideReason: '',
      form: {
        outcome_id: null,
        outcome_observation: '',
      },
    }
  },

  computed: {
    canOverride() {
      return this.$hasAbility('regra.dispensar')
    },

    hasOverridable() {
      return this.violations.some(v => v.allows_override)
    },

    outcomeName() {
      const found = this.outcomes.find(o => o.id === this.form.outcome_id)
      return found ? found.name : ''
    },

    confirmLabel() {
      if (this.requiresConfirmation) return 'Confirmar mesmo assim'
      return 'Confirmar Finalização'
    },
  },

  methods: {
    onShow() {
      this.showErrors = false
      this.form.outcome_id = null
      this.form.outcome_observation = ''
      this.clearPendencies()
      this.loadOutcomes()
    },

    clearPendencies() {
      this.violations = []
      this.warnings = []
      this.requiresConfirmation = false
      this.overrideIds = []
      this.overrideReason = ''
    },

    canOpen(violation) {
      return !!(violation.occurrence_involved_id && violation.target);
    },

    toggleOverride(ruleId) {
      const i = this.overrideIds.indexOf(ruleId)
      if (i >= 0) this.overrideIds.splice(i, 1)
      else this.overrideIds.push(ruleId)
    },

    loadOutcomes() {
      this.loadingOutcomes = true
      let url = `${this.$store.state.api}outcomes/for-context?context=Ocorrencia`
      if (this.disciplineId) url += `&discipline_id=${this.disciplineId}`
      axios
        .get(url)
        .then(r => {
          this.outcomes = r.data.data || []
        })
        .catch(() => {})
        .finally(() => {
          this.loadingOutcomes = false
        })
    },

    async confirm() {
      this.showErrors = true
      if (!this.form.outcome_id || this.saving) return

      this.saving = true

      try {
        await axios.post(
          `${this.$store.state.api}occurrences/${this.occurrenceId}/finalize`,
          {
            outcome_id: this.form.outcome_id,
            outcome_observation: this.form.outcome_observation || null,
            confirm_warnings: this.requiresConfirmation,
            override_rule_ids: this.overrideIds,
            override_reason: this.overrideReason || null,
          },
        )

        this.clearPendencies()
        this.$emit('update:visible', false)
        this.$emit('finalized')
      } catch (e) {
        const data = (e.response && e.response.data) || {}

        if (e.response && e.response.status === 422 && data.allowed === false) {
          this.violations = data.errors || []
          this.warnings = data.warnings || []
          this.requiresConfirmation = !!data.requires_confirmation
          return
        }

        this.$message('Erro', this.apiErrorText(e, 'Erro ao finalizar.'), 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.finalize-warn {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fdf3e4;
  color: #b45309;
  font-size: 12.5px;
}
.dark-layout .finalize-warn {
  background: #2e2214;
  color: #e5a95c;
}
.finalize-modal-warning {
  display: flex;
  align-items: flex-start;
  background: #fff8e6;
  border-left: 3px solid #ff9f43;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 0.87rem;
  color: #7d5a00;
  font-weight: 500;
  line-height: 1.5;
}

.finalize-pendencies {
  background: #fdeaea;
  border-left: 3px solid #ea5455;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}
.dark-layout .finalize-pendencies {
  background: #2c1a1b;
}
.finalize-warn-block {
  background: #fff8e6;
  border-left: 3px solid #ff9f43;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}
.dark-layout .finalize-warn-block {
  background: #2e2214;
}
.finalize-pendencies-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.87rem;
  margin-bottom: 6px;
}
.finalize-pendencies-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.83rem;
  line-height: 1.6;
}

.required {
  color: #dc3545;
}
</style>
